import {
  AppBar,
  IconButton,
  Stack,
  SxProps,
  Toolbar,
  Typography
} from '@mui/material';
import { Button } from 'system/atoms';
import { PoppIcon } from 'common/icons/PoppIcon';
import { Link, useLocation } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { useNavBarOptions } from '../hooks/useNavBarOptions';
import { AtsConnector } from 'services/integration/components/AtsConnector';
import { AccountsDropDown } from 'components/AccountsDropDown';
import { AccountCircle, Code, Help } from '@mui/icons-material';
import { WorkflowBuilderIcon } from 'common/icons/WorkflowBuilderIcon';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import {
  developerPortalLink,
  supportLink,
  workflowBuilderLink
} from '../constants/navbar';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import { organizationGroupOrganizationsAtom } from 'services/organizationGroupOrganizations/atom';
import { RouterLink } from 'common/ui/RouterLink';
import { MainLogo } from 'common/ui/MainLogo';
import { PathsV2 } from 'core/services/navigation/pathsV2';
import { hideLinksForOrganization } from 'modules/layout/utils/hideLinksForOrganization';
import { buildWorkflowBuilderUrl } from 'modules/layout/utils/buildWorkflowBuilderUrl';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { candidatesLandingPath } from 'modules/layout/constants/candidatesLandingPath';
import { useIsHomeHidden } from 'modules/layout/hooks/useIsHomeHidden';

const buttonSx: SxProps = {
  width: 112,
  height: '56px',
  marginRight: 1,
  color: 'white',
  borderRadius: 0,
  ':hover': {
    backgroundColor: 'customGrey.light',
    color: 'customGrey.dark'
  }
};

const selectedButtonSx: SxProps = {
  backgroundColor: 'background.default',
  color: 'text.primary',
  fontWeight: 'bold',
  textDecoration: 'none',
  ':hover': {
    backgroundColor: 'customGrey.light',
    color: 'customGrey.dark'
  }
};

const developerPortalIconSx: SxProps = {
  fontSize: 12
};

const navIconButtonSx: SxProps = {
  backgroundColor: 'white',
  borderRadius: '50%',
  width: 20,
  height: 20,
  padding: '3px',
  alignSelf: 'center',
  '&:hover': {
    backgroundColor: 'grey.200'
  }
};

const toolbarSx: SxProps = {
  width: '100%',
  justifyContent: 'space-between',
  px: 0
};

export type NavLink = {
  to: string;
  label: string;
  selectedPaths: string[];
};

const defaultNavLinks: NavLink[] = [
  {
    to: Paths.HOME,
    label: 'Home',
    selectedPaths: [Paths.HOME]
  },
  {
    to: Paths.CAMPAIGNS,
    label: 'Campaigns',
    selectedPaths: [Paths.CAMPAIGNS, Paths.CAMPAIGN]
  },
  {
    to: Paths.CANDIDATES_RANKING,
    label: 'Analyse',
    selectedPaths: [Paths.CANDIDATES_RANKING]
  },
  {
    to: candidatesLandingPath,
    label: 'Candidates',
    selectedPaths: [PathsV2.CANDIDATE_PROFILE_LIST]
  }
];

const NavLinkButton: React.FC<NavLink> = ({ to, label, selectedPaths }) => {
  const location = useLocation();

  const pathIsSelected = selectedPaths.some((path) =>
    location.pathname.includes(path)
  );

  let navButtonSx = buttonSx;

  if (pathIsSelected) {
    navButtonSx = {
      ...navButtonSx,
      ...selectedButtonSx
    };
  }

  return (
    <Button
      component={Link}
      to={to}
      variant="tertiary"
      sx={navButtonSx}
      data-testid={label}
    >
      {label}
    </Button>
  );
};

export const PrivateNavBar: React.FC = () => {
  const { isAuthenticatedAndOnboard } = useNavBarOptions();
  const { track } = useTracking();
  const isWorkflowBuilderEnabled = useFlag(Flags.WORKFLOW_BUILDER);
  const isCampaignsTabHidden = Boolean(useFlag(Flags.HIDE_CAMPAIGNS_TAB));
  const isAnalyseTabHidden = Boolean(useFlag(Flags.HIDE_ANALYSE_TAB));
  const isHomeTabHidden = useIsHomeHidden();
  const location = useLocation();
  const organization = useRecoilValue(organizationAtom);
  const organizationConfig = useOrganizationConfig();
  const visibleOrganizations = useRecoilValue(
    organizationGroupOrganizationsAtom
  );
  const hasVisibleAccounts =
    visibleOrganizations !== null && visibleOrganizations.length > 0;

  const isProfileSelected = location.pathname.includes(Paths.PROFILE);

  const isAdministratorView = useMemo(() => {
    return location.pathname.includes(Paths.ADMINISTRATOR);
  }, [location.pathname]);

  const isPublic = useMemo(() => {
    return location.pathname.includes('/public');
  }, [location.pathname]);

  if (isPublic) {
    return null;
  }

  const navLinks = organization
    ? hideLinksForOrganization({
        links: defaultNavLinks,
        organizationConfig,
        isHomeTabHidden,
        isCampaignsTabHidden,
        isAnalyseTabHidden
      })
    : defaultNavLinks;

  return (
    <AppBar position="sticky" component="nav" elevation={0}>
      {!isAdministratorView && organization && (
        <RouterLink to={Paths.HOME}>
          <MainLogo />
        </RouterLink>
      )}
      {(isAdministratorView || !organization) && <PoppIcon />}
      {isAuthenticatedAndOnboard && (
        <Toolbar sx={toolbarSx} disableGutters>
          <Stack direction="row">
            {isAdministratorView && (
              <Typography sx={{ fontWeight: 'bold' }}>Administrator</Typography>
            )}
            {!isAdministratorView && hasVisibleAccounts && (
              <>
                {navLinks.map((navLink) => (
                  <NavLinkButton key={navLink.to} {...navLink} />
                ))}
              </>
            )}
          </Stack>
          <Stack direction="row" gap={3}>
            {!isAdministratorView && hasVisibleAccounts && <AtsConnector />}
            <IconButton
              component={Link}
              to={Paths.PROFILE}
              sx={{
                color: isProfileSelected ? 'primary.main' : 'white'
              }}
              onClick={() => track(TrackingEventName.Profile)}
            >
              <AccountCircle fontSize="small" />
            </IconButton>
            {isWorkflowBuilderEnabled && (
              <IconButton
                sx={navIconButtonSx}
                onClick={() => {
                  window.open(
                    buildWorkflowBuilderUrl(
                      workflowBuilderLink,
                      organization?.id
                    ),
                    '_blank'
                  );
                }}
              >
                <WorkflowBuilderIcon size={10} />
              </IconButton>
            )}
            <IconButton
              sx={navIconButtonSx}
              onClick={() => {
                window.open(developerPortalLink, '_blank');
              }}
            >
              <Code sx={developerPortalIconSx} />
            </IconButton>
            <IconButton
              sx={{
                color: 'white'
              }}
              onClick={() => {
                track(TrackingEventName.Help);
                window.open(supportLink, '_blank');
              }}
            >
              <Help fontSize="medium" />
            </IconButton>
            {!isAdministratorView && organization && <AccountsDropDown />}
          </Stack>
        </Toolbar>
      )}
    </AppBar>
  );
};

export const withPrivateNavbar = (element: React.ReactElement) => {
  return (
    <>
      <PrivateNavBar />
      {element}
    </>
  );
};
